import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import {
  buildCompiledAssetUrl,
  buildRuntimeShimPath,
  buildRuntimeShimUrl,
} from '@mutiny-pkg/bin-ui/src/customComponents/runtimeShimUrl';

type CustomComponentAssetUrlResolverInput = {
  renderStage: RenderStage;
  staticSiteDomain: string | null;
  getPageOrigin: () => string;
};

type CustomComponentAssetKind = 'compiled' | 'runtime';

export function createCustomComponentAssetUrlResolvers({
  renderStage,
  staticSiteDomain,
  getPageOrigin,
}: CustomComponentAssetUrlResolverInput) {
  if (renderStage === RenderStage.Publish) {
    return {
      resolveCompiledUrl: (sourceHash: string) => new URL(`/assets/${sourceHash}.js`, getPageOrigin()).toString(),
      resolveRuntimeUrl: (specifier: string) => new URL(buildRuntimeShimPath(specifier), getPageOrigin()).toString(),
    };
  }

  // Preview / in-app render: the CDN base comes from the page group and can be absent (group
  // unloaded). Never interpolate a null/undefined base into an artifact URL (production fetched
  // `https://null/...`) — return `null` so the loader skips the load, and record one structured
  // console event per kind. Published pages run no PostHog client, so console is the record; the
  // loader's typed `CustomComponentAssetUrlUnavailableError` surfaces the failure to the slot.
  const reportedKinds = new Set<CustomComponentAssetKind>();
  const reportUnavailable = (kind: CustomComponentAssetKind, ref: string) => {
    if (reportedKinds.has(kind)) return;
    reportedKinds.add(kind);
    console.warn(
      JSON.stringify({
        event: 'custom_component_asset_url_unavailable',
        asset_kind: kind,
        ref,
        surface: 'published-page',
        static_assets_base_url_missing: true,
      }),
    );
  };

  return {
    resolveCompiledUrl: (sourceHash: string): string | null => {
      const url = buildCompiledAssetUrl(staticSiteDomain, sourceHash);
      if (url === null) reportUnavailable('compiled', sourceHash);
      return url;
    },
    resolveRuntimeUrl: (specifier: string): string | null => {
      const url = buildRuntimeShimUrl(staticSiteDomain, specifier);
      if (url === null) reportUnavailable('runtime', specifier);
      return url;
    },
  };
}
